import { saveAppearance } from './appearance-store';
import { HERO_PRESETS, buildHeroAppearance } from './presets';

/**
 * A hero an operator applied from the admin player card.
 *
 * The look itself lives in this browser (appearance-store), so the server only
 * says WHICH preset and WHEN. A hero is applied once per assignment: the stamp
 * of the last one applied is kept beside the look, and only a newer assignment
 * re-dresses the character — the player can still change their look afterwards
 * without the admin's choice coming back on every load.
 */
const stampKey = (userKey: string) => 'terraboma:hero-applied:' + userKey;

export interface AssignedHero { heroPreset: string | null; heroAssignedAt: string | null }

/** Pure decision, exported for tests: should this assignment be applied now? */
export function shouldApplyHero(a: AssignedHero, lastApplied: string | null): boolean {
  if (!a.heroPreset || !a.heroAssignedAt) return false;
  if (!HERO_PRESETS.some((p) => p.id === a.heroPreset)) return false;
  const at = Date.parse(a.heroAssignedAt);
  if (Number.isNaN(at)) return false;
  const last = lastApplied ? Date.parse(lastApplied) : NaN;
  return Number.isNaN(last) || at > last;
}

/** Fetch and apply. Resolves true when the look was changed. Never throws. */
export async function syncAssignedHero(userKey: string, fetchHero: () => Promise<AssignedHero>): Promise<boolean> {
  try {
    const a = await fetchHero();
    let last: string | null = null;
    try { last = localStorage.getItem(stampKey(userKey)); } catch { /* storage blocked */ }
    if (!shouldApplyHero(a, last)) return false;
    const preset = HERO_PRESETS.find((p) => p.id === a.heroPreset)!;
    saveAppearance(userKey, buildHeroAppearance(preset));
    try { localStorage.setItem(stampKey(userKey), a.heroAssignedAt!); } catch { /* storage blocked */ }
    return true;
  } catch {
    return false;
  }
}
